import { supabase } from "@/integrations/supabase/client";
import { parseUserFile } from "@/lib/file-parsing";
import { categoricalColumns, numericColumns } from "@/lib/ml";

export interface SourceOption {
  id: string;
  label: string;
  kind: "file" | "run";
}

/** Real data sources the user owns: stored files and completed workflow runs. */
export async function listDataSources(): Promise<SourceOption[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const [{ data: files }, { data: runs }] = await Promise.all([
    supabase
      .from("file_storage")
      .select("id, file_name")
      .eq("user_id", user.id)
      .eq("is_folder", false)
      .order("created_at", { ascending: false })
      .limit(30),
    supabase
      .from("workflow_runs")
      .select("id, input_source, created_at")
      .eq("user_id", user.id)
      .eq("status", "completed")
      .order("created_at", { ascending: false })
      .limit(10),
  ]);

  return [
    ...(files ?? []).map((f) => ({ id: `file:${f.id}`, label: f.file_name, kind: "file" as const })),
    ...(runs ?? []).map((r) => ({
      id: `run:${r.id}`,
      label: `${r.input_source || "Workflow data"} — ${new Date(r.created_at).toLocaleDateString()}`,
      kind: "run" as const,
    })),
  ];
}

/** Load the actual rows behind a source option. Throws when there is nothing real to read. */
export async function loadSourceRows(sourceId: string): Promise<Record<string, any>[]> {
  const [kind, id] = sourceId.split(":");

  if (kind === "run") {
    const { data, error } = await supabase
      .from("workflow_data")
      .select("row_data")
      .eq("workflow_run_id", id)
      .order("row_index", { ascending: true })
      .limit(5000);
    if (error) throw error;
    const rows = (data ?? []).map((d) => d.row_data as Record<string, any>);
    if (!rows.length) throw new Error("That run has no stored rows.");
    return rows;
  }

  const { data: file, error: fileError } = await supabase
    .from("file_storage")
    .select("file_name, file_path, mime_type")
    .eq("id", id)
    .maybeSingle();
  if (fileError) throw fileError;
  if (!file) throw new Error("That file is no longer available.");

  const { data: blob, error } = await supabase.storage.from("user_files").download(file.file_path);
  if (error) throw error;
  const parsed = await parseUserFile(
    new File([blob], file.file_name, { type: file.mime_type ?? undefined }),
  );
  if (!parsed.rows?.length) throw new Error("No table rows could be read from that file.");
  return parsed.rows;
}

const toNumber = (v: any): number | null => {
  if (typeof v === "number") return isFinite(v) ? v : null;
  const n = Number(String(v ?? "").replace(/[₦$£€,%\s]/g, ""));
  return isNaN(n) ? null : n;
};

/** Sum a numeric column grouped by a label column — real aggregation, no sampling. */
export function aggregate(
  rows: Record<string, any>[],
  xField: string,
  yField: string,
  how: "sum" | "avg" | "count" = "sum",
  limit = 20,
) {
  const buckets = new Map<string, { total: number; count: number }>();
  rows.forEach((r) => {
    const key = String(r[xField] ?? "Unknown");
    const val = how === "count" ? 1 : toNumber(r[yField]);
    if (val === null) return;
    const b = buckets.get(key) ?? { total: 0, count: 0 };
    b.total += val;
    b.count += 1;
    buckets.set(key, b);
  });
  return Array.from(buckets.entries())
    .map(([name, b]) => ({
      [xField]: name,
      [yField]: Number((how === "avg" ? b.total / b.count : b.total).toFixed(2)),
    }))
    .sort((a, b) => Number(b[yField]) - Number(a[yField]))
    .slice(0, limit);
}

export interface ChartRecipe {
  title: string;
  chartType: "bar" | "line" | "pie";
  xField: string;
  yField: string;
  aggregation: "sum" | "avg" | "count";
  data: Record<string, any>[];
}

const looksLikeDate = (rows: Record<string, any>[], col: string) =>
  /date|month|day|time|period|week|year/i.test(col) ||
  rows.slice(0, 10).every((r) => r[col] && !isNaN(Date.parse(String(r[col]))));

/**
 * Build real chart recipes for a template by matching the template's intent to
 * the columns that actually exist in the user's data. Returns [] when the data
 * cannot support the template — we never fall back to invented numbers.
 */
export function buildRecipes(
  rows: Record<string, any>[],
  template: "trends" | "breakdown" | "performance",
): ChartRecipe[] {
  const numeric = numericColumns(rows);
  const categorical = categoricalColumns(rows, 15).filter((c) => !numeric.includes(c));
  if (!numeric.length) return [];

  const dateCol = categorical.find((c) => looksLikeDate(rows, c));
  const labelCol = categorical.find((c) => c !== dateCol);
  const recipes: ChartRecipe[] = [];

  if (template === "trends" && dateCol) {
    numeric.slice(0, 2).forEach((m) =>
      recipes.push({
        title: `${m} over ${dateCol}`,
        chartType: "line",
        xField: dateCol,
        yField: m,
        aggregation: "sum",
        data: aggregate(rows, dateCol, m, "sum", 50),
      }),
    );
  }

  if (template === "breakdown" && labelCol) {
    const m = numeric[0];
    recipes.push({
      title: `${m} by ${labelCol}`,
      chartType: "pie",
      xField: labelCol,
      yField: m,
      aggregation: "sum",
      data: aggregate(rows, labelCol, m, "sum", 8),
    });
  }

  if (template === "performance") {
    const groupCol = labelCol ?? dateCol;
    if (groupCol) {
      numeric.slice(0, 2).forEach((m) =>
        recipes.push({
          title: `Average ${m} by ${groupCol}`,
          chartType: "bar",
          xField: groupCol,
          yField: m,
          aggregation: "avg",
          data: aggregate(rows, groupCol, m, "avg", 12),
        }),
      );
    }
  }

  return recipes.filter((r) => r.data.length > 0);
}

/** KPI values computed from the user's rows. */
export function computeKpi(
  rows: Record<string, any>[],
  field: string,
  how: "sum" | "avg" | "count" | "max" | "min",
) {
  const vals = rows.map((r) => toNumber(r[field])).filter((v): v is number => v !== null);
  if (how === "count") return rows.length;
  if (!vals.length) return 0;
  switch (how) {
    case "sum":
      return Number(vals.reduce((a, b) => a + b, 0).toFixed(2));
    case "avg":
      return Number((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2));
    case "max":
      return Math.max(...vals);
    case "min":
      return Math.min(...vals);
  }
}
